import { Card } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { MetricDeltaRow } from "@/components/dashboard/MetricDeltaRow";

function fmt1(n: number) {
  return n.toFixed(1);
}

export interface KpiCardProps {
  label: string;
  unit: string;
  current: number;
  priorYear: number;
  nva?: number;
  deltaUnit: string;
  // Cards showing a rounded, comma-grouped headline (e.g. "1,234" or "$892,400")
  // distinct from the raw number the delta rows compute against.
  displayValue?: string;
  // Overrides the delta rows' default `${n.toFixed(1)} ${deltaUnit}` rendering
  // — e.g. currency formatting for revenue cards.
  formatValue?: (n: number) => string;
  // Hover text, for cards whose label cannot carry the whole definition. The
  // Capacity cards need it to say they measure the ROSTER while the Utilization
  // cards measure hours actually worked — two similar-looking figures that no
  // longer agree, and the difference has to be reachable from the card itself.
  definition?: string;
  // Shortens the delta rows to "PY" / "NVA" in a 30px label column. Only the
  // Revenue tab's five-across layout needs it; see MetricDeltaRow.
  compactDeltas?: boolean;
}

export function KpiCard({
  label,
  unit,
  current,
  priorYear,
  nva,
  deltaUnit,
  displayValue,
  formatValue,
  definition,
  compactDeltas,
}: KpiCardProps) {
  const card = (
    <Card className="flex-1 bg-white rounded-lg px-3.5 py-4 gap-0 shadow-none border border-black/10">
      {/* Two lines of height whether the label needs them or not: labels of
          unequal length otherwise start the headline figures at different
          heights, and a row of cards whose big numbers sit on three different
          baselines reads as broken rather than as varied. */}
      <div
        className="text-[12px] mb-1 font-medium leading-[1.35] min-h-[2.7em]"
        style={{ color: "#333" }}
      >
        {label}
      </div>
      <div className="text-[28px] font-semibold tracking-tight leading-none">
        {displayValue ?? fmt1(current)}
        <span className="text-[14px] font-normal ml-0.5">{unit}</span>
      </div>
      <div className="h-px bg-gray-200 my-2" />
      <div className="flex flex-col gap-1">
        <MetricDeltaRow
          label={compactDeltas ? "PY" : "vs. PY"}
          current={current}
          baseline={priorYear}
          unit={deltaUnit}
          formatValue={formatValue}
          compactLabel={compactDeltas}
        />
        {nva !== undefined && (
          <MetricDeltaRow
            label={compactDeltas ? "NVA" : "vs. NVA avg"}
            current={current}
            baseline={nva}
            unit={deltaUnit}
            formatValue={formatValue}
            compactLabel={compactDeltas}
          />
        )}
      </div>
    </Card>
  );

  if (!definition) return card;
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{card}</TooltipTrigger>
        <TooltipContent
          side="top"
          showArrow={false}
          className="max-w-[280px] border border-black/10 bg-white text-gray-600 shadow-lg"
        >
          {definition}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
